// ExportArticleExcelOneSheet.jsx
import * as XLSX from "xlsx-js-style";

/** ===== Estilos ===== */
const BORDER_THIN = {
  top: { style: "thin", color: { rgb: "FFBFBFBF" } },
  left: { style: "thin", color: { rgb: "FFBFBFBF" } },
  bottom: { style: "thin", color: { rgb: "FFBFBFBF" } },
  right: { style: "thin", color: { rgb: "FFBFBFBF" } },
};

const HEADER_STYLE = {
  font: { bold: true },
  alignment: { horizontal: "center", vertical: "center", wrapText: true },
  fill: { fgColor: { rgb: "FFEFEFEF" } },
  border: BORDER_THIN,
};

const CELL_STYLE = {
  alignment: { vertical: "center", wrapText: true },
  border: BORDER_THIN,
};

const NUMBER_STYLE = {
  ...CELL_STYLE,
  numFmt: "0.00",
};

/** ===== Utils ===== */
const fmtDate = (v) => {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const fmtBool = (v) => (v === true ? "Sí" : v === false ? "No" : "");

const fmtNumStr2 = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "";
};

const safeArr = (v) => (Array.isArray(v) ? v : []);

/** ===== Columnas base ===== */
const BASE_COLUMNS = [
  { key: "id", header: "ID", type: "text" },
  { key: "titlees", header: "Título", type: "text" },
  { key: "subtitlees", header: "Subtítulo", type: "text" },
  { key: "owncode", header: "Código interno", type: "text" },
  { key: "codean", header: "Código EAN", type: "text" },
  { key: "barcode", header: "Código de barras", type: "text" },
  { key: "accountingcode", header: "Código Contable", type: "text" },
  { key: "salesunits_namees", header: "Unidad venta", type: "text" },
  { key: "department_namees", header: "Departamento", type: "text" },
  { key: "section_namees", header: "Sección", type: "text" },
  { key: "subsection_namees", header: "Grupo", type: "text" },
  { key: "typesoftax_namees", header: "Tipo de impuesto", type: "text" },
  { key: "typesoftax_tax", header: "% impuesto", type: "number" },
  { key: "typesoftax_taxinclude", header: "Impuesto incluido", type: "bool" },
  { key: "codeler_code", header: "Código L.E.R.", type: "text" },
];

/** ===== Columnas extendidas ===== */
const BASE_COLUMNS_EXTENDED = [
  { key: "id", header: "ID", type: "text" },
  { key: "titlees", header: "Descripción", type: "text" },
  { key: "subtitlees", header: "Subtítulo", type: "text" },
  { key: "owncode", header: "Código interno", type: "text" },
  { key: "codean", header: "Código EAN", type: "text" },
  { key: "barcode", header: "Código de barras", type: "text" },
  { key: "accountingcode", header: "Código Contable", type: "text" },
  { key: "marca", header: "Marca", type: "text" },
  { key: "model", header: "Modelo", type: "text" },
  { key: "location", header: "Ubicación", type: "text" },
  { key: "applytypesofsize", header: "Aplica tallas", type: "bool" },
  { key: "applycolor", header: "Aplica color", type: "bool" },
  { key: "applynumserial", header: "Aplica nº serie", type: "bool" },
  { key: "isnewpiece", header: "Es pieza nueva", type: "bool" },
  { key: "modifystock", header: "Modifica stock", type: "bool" },
  { key: "minimumstock", header: "Stock mínimo", type: "number" },
  { key: "maximumstock", header: "Stock máximo", type: "number" },
  { key: "actualstock", header: "Stock actual", type: "number" },
  { key: "averagepurchaseprice", header: "Precio medio compra", type: "number" },
  { key: "lastpurchaseprice", header: "Último precio compra", type: "number" },
  { key: "datelastpurchaseprice", header: "Fecha último precio", type: "date" },
  { key: "salesunits_namees", header: "Unidad venta", type: "text" },
  { key: "department_namees", header: "Departamento", type: "text" },
  { key: "section_namees", header: "Sección", type: "text" },
  { key: "subsection_namees", header: "Grupo", type: "text" },
  {
    key: "defaultprovider_tradename",
    header: "Proveedor por defecto (nombre comercial)",
    type: "text",
  },
  {
    key: "defaultprovider_documentid",
    header: "Proveedor por defecto (NIF/CIF)",
    type: "text",
  },
  {
    key: "lastprovider_tradename",
    header: "Último proveedor (nombre comercial)",
    type: "text",
  },
  {
    key: "lastprovider_documentid",
    header: "Último proveedor (NIF/CIF)",
    type: "text",
  },
  { key: "typesoftax_namees", header: "Tipo de impuesto", type: "text" },
  { key: "typesoftax_tax", header: "% impuesto", type: "number" },
  { key: "typesoftax_taxinclude", header: "Impuesto incluido", type: "bool" },
  { key: "codeler_code", header: "Código L.E.R.", type: "text" },
];

/** ===== Helpers ===== */
const getCellValueByType = (value, type) => {
  switch (type) {
    case "date":
      return fmtDate(value);
    case "bool":
      return fmtBool(value);
    case "number":
      return value == null || value === "" ? "" : Number(value);
    default:
      return value ?? "";
  }
};

const buildPricesCell = (row) => {
  const prices = safeArr(row?.articlestoretypesofprices);
  if (prices.length === 0) return "";

  return prices
    .map((p) => {
      const type = (p?.typesofprice_namees ?? "").toString();
      const importStr = fmtNumStr2(p?.saleprice);
      return `${type} | ${importStr}`;
    })
    .join("\n");
};

/** ===== Exportar ===== */
const exportArticleExcelOneSheet = (data, fileName, showExtendedFilter) => {
  const items = Array.isArray(data) ? data : [];
  if (items.length === 0) return;

  const selectedBaseColumns = showExtendedFilter ? BASE_COLUMNS_EXTENDED : BASE_COLUMNS;

  const COLUMNS = [
    ...selectedBaseColumns,
    { key: "__PRICES__", header: "Precios (tipo | importe)", type: "text" },
  ];

  const headers = COLUMNS.map((c) => c.header);

  const rows = items.map((row) =>
    COLUMNS.map((column) => {
      if (column.key === "__PRICES__") {
        return buildPricesCell(row);
      }

      return getCellValueByType(row?.[column.key], column.type);
    })
  );

  const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

  /** Encabezados */
  headers.forEach((header, colIndex) => {
    const addr = XLSX.utils.encode_cell({ r: 0, c: colIndex });
    ws[addr] = ws[addr] || { t: "s", v: header };
    ws[addr].s = HEADER_STYLE;
  });

  /** Celdas */
  const range = XLSX.utils.decode_range(ws["!ref"]);

  for (let r = 1; r <= range.e.r; r++) {
    for (let c = 0; c < COLUMNS.length; c++) {
      const addr = XLSX.utils.encode_cell({ r, c });
      const cell = ws[addr];
      if (!cell) continue;

      const column = COLUMNS[c];
      cell.s = { ...CELL_STYLE };

      if (column.type === "number" && cell.v !== "" && Number.isFinite(Number(cell.v))) {
        cell.t = "n";
        cell.v = Number(cell.v);
        cell.z = "0.00";
        cell.s = { ...NUMBER_STYLE };
      }
    }
  }

  /** Auto ancho columnas */
  const aoa = [headers, ...rows];
  ws["!cols"] = COLUMNS.map((column, i) => {
    const maxLen = aoa.reduce((max, row) => {
      const value = row[i];
      const str =
        typeof value === "number"
          ? Number.isFinite(value)
            ? value.toFixed(2)
            : ""
          : (value ?? "").toString();

      const extra = column.key === "__PRICES__" ? 20 : 0;
      return Math.max(max, str.length + extra);
    }, headers[i].length);

    return { wch: Math.min(100, maxLen + 2) };
  });

  /** Altura automática aproximada para filas con saltos de línea */
  ws["!rows"] = [ { hpx: 28 } ];

  rows.forEach((row, index) => {
    const maxLines = row.reduce((acc, value) => {
      const lines = String(value ?? "").split("\n").length;
      return Math.max(acc, lines);
    }, 1);

    ws["!rows"][index + 1] = { hpx: Math.max(24, maxLines * 18) };
  });

  /** Congelar cabecera */
  ws["!freeze"] = { xSplit: 0, ySplit: 1 };

  /** Crear libro */
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "ARTÍCULOS");

  const safeName = fileName?.endsWith(".xlsx")
    ? fileName
    : `${fileName || "ARTICULOS"}.xlsx`;

  const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = safeName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
};

export default exportArticleExcelOneSheet;